import React from 'react';
import styled from 'styled-components';
import { Container } from '../../styles/components';
import { theme } from '../../styles/theme';
import heroBackgroundImg from '../../assets/images/hero-bg-new.jpg';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  height?: string;
  textAlign?: 'left' | 'center' | 'right';
  children?: React.ReactNode;
}

// Using local image for hero background
const heroBackgroundImage = heroBackgroundImg;

const HeroSection = styled.section<{
  height: string;
}>`
  position: relative;
  padding: ${theme.spacing.xxxl} 0;
  color: ${theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => props.height};
  margin-bottom: ${theme.spacing.xl};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(166, 124, 0, 0.75) 100%
    ),
    url('${heroBackgroundImage}');
    background-size: cover;
    background-position: center;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(212, 175, 55, 0.3), transparent 60%);
    z-index: -1;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background: linear-gradient(to top, ${theme.colors.background}, transparent);
  z-index: 0;
`;

const HeroContent = styled.div<{ textAlign: string }>`
  position: relative;
  z-index: 1;
  text-align: ${props => props.textAlign};
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
  position: relative;
  display: inline-block;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background: ${theme.colors.gradient.gold};
    border-radius: ${theme.borderRadius.pill};
    box-shadow: 0 0 10px rgba(212, 175, 55, 0.5);
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: ${theme.spacing.lg} auto 0;
  line-height: 1.6;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
`;

const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  height = '40vh',
  textAlign = 'center',
  children
}) => {
  return (
    <HeroSection 
      height={height}
    >
      <HeroOverlay />
      <Container>
        <HeroContent textAlign={textAlign}>
          <HeroTitle>{title}</HeroTitle>
          {subtitle && <HeroSubtitle>{subtitle}</HeroSubtitle>}
          {children}
        </HeroContent>
      </Container>
    </HeroSection>
  );
};

export default PageHero; 